import { AvatarDropdown, AvatarName, Footer, Question, SelectLang } from '@/components';
import { currentUser as queryCurrentUser } from '@/services/ant-design-pro/api';
import { LinkOutlined } from '@ant-design/icons';
import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import { SettingDrawer } from '@ant-design/pro-components';
import type { RunTimeLayoutConfig } from '@umijs/max';
import { history, Link } from '@umijs/max';
import React from 'react';
import defaultSettings from '../config/defaultSettings';
import { errorConfig } from './requestErrorConfig';

const isDev = true;
const loginPath = '/user/login';

/**
 * @see  https://umijs.org/zh-CN/plugins/plugin-initial-state
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: API.CurrentUser;
  loading?: boolean;
  fetchUserInfo?: (player_id: number, accessToken: string) => Promise<API.CurrentUser | undefined>;
}> {
  const fetchUserInfo = async (player_id: number, accessToken: string) => {
    try {
      const userInfo = await queryCurrentUser(player_id, accessToken, {
        skipErrorHandler: true,
      });
      if (userInfo.success && userInfo.data) {
        return userInfo.data;
      }
    } catch (error) {
      history.push(loginPath);
    }
    return undefined;
  };

  // 如果不是登录页面，执行
  const { location } = history;
  const accessToken = localStorage.getItem('accessToken');
  const player_idStr = localStorage.getItem('player_id');
  const player_id = player_idStr ? parseInt(player_idStr, 10) : undefined;
  if (location.pathname !== loginPath) {
    const currentUser = await fetchUserInfo(player_id!, accessToken!);
    return {
      fetchUserInfo,
      currentUser,
      settings: defaultSettings as Partial<LayoutSettings>,
    };
  }
  return {
    fetchUserInfo,
    settings: defaultSettings as Partial<LayoutSettings>,
  };
}

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({ initialState, setInitialState }) => {
  return {
    actionsRender: () => [<Question key="doc" />, <SelectLang key="SelectLang" />],
    avatarProps: {
      src: initialState?.currentUser?.avatar,
      title: <AvatarName />,
      render: (_, avatarChildren) => {
        return <AvatarDropdown>{avatarChildren}</AvatarDropdown>;
      },
    },
    waterMarkProps: {
      content: [initialState?.currentUser?.name || '', '灵丌管理中台']
    },
    footerRender: () => <Footer />,
    onPageChange: () => {
      const { location } = history;
      // 如果没有登录，重定向到 login
      if (!initialState?.currentUser && location.pathname !== loginPath) {
        history.push(loginPath);
      }
    },
    bgLayoutImgList: [
      {
        src: '/images/D2LWSqNny4sAAAAAAAAAAAAAFl94AQBr.png',
        left: 85,
        bottom: 100,
        height: '303px',
      },
      {
        src: '/images/C2TWRpJpiC0AAAAAAAAAAAAAFl94AQBr.png',
        bottom: -68,
        right: -45,
        height: '303px',
      },
      {
        src: '/images/F6vSTbj8KpYAAAAAAAAAAAAAFl94AQBr.png',
        bottom: 0,
        left: 0,
        width: '331px',
      },
    ],
    //TODO 放在这里
    links: isDev
      ? [
          <a key="001code" href="https://www.001code.com" target="_blank">
            <LinkOutlined />
            <span>回到001CODE</span>
          </a>,
        ]
      : [],
    menuHeaderRender: undefined,
    // 自定义 403 页面
    // unAccessible: <div>unAccessible</div>,
    // 增加一个 loading 的状态
    childrenRender: (children) => {
      // if (initialState?.loading) return <PageLoading />;
      return (
        <>
          {children}
          {isDev && (
            <SettingDrawer
              disableUrlParams
              enableDarkTheme
              settings={initialState?.settings}
              onSettingChange={(settings) => {
                setInitialState((preInitialState) => ({
                  ...preInitialState,
                  settings,
                }));
              }}
            />
          )}
        </>
      );
    },
    ...initialState?.settings,
  };
};

/**
 * @name request 配置，可以配置错误处理
 * 它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 */
export const request = {
  ...errorConfig,
};
